import { zodResolver } from '@hookform/resolvers/zod';
import { Save as SaveIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import coursesApi from 'Api/axios/courses-api';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import { sessionFormDefaultValues, sessionFormSchema } from '../schemas/session-form-schemas';
import { localToUtcIso, utcIsoToLocalParts } from '../utils/session-datetime';
import UserAutocomplete from './UserAutocomplete';

const styles = {
  paper: { borderRadius: 2 },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    flexWrap: 'wrap',
  },
  title: { fontSize: 18, fontWeight: 600, color: colors.BLACK, m: 0 },
  draftPill: {
    height: 22,
    fontSize: 11,
    fontWeight: 500,
    bgcolor: colors.BLUE_GRAY,
    color: colors.GRAY_DARK,
    border: `1px solid ${colors.GRAY_HR}`,
  },
  subtitle: { fontSize: 13, color: colors.GRAY_DARK, mt: 0.5 },
  subtitleAccent: { color: colors.GRAY_DARKER, fontWeight: 500 },
  rowDate: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr 1fr' },
    gap: 2,
  },
  fieldLabel: { fontSize: 12, color: colors.GRAY_DARK, mb: 0.75, fontWeight: 500 },
  toggleGroup: {
    mb: 1.5,
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontSize: 12.5,
      fontWeight: 500,
      px: 1.5,
      border: `1px solid ${colors.GRAY_HR}`,
      color: colors.GRAY_DARKER,
      '&.Mui-selected': {
        bgcolor: colors.GREEN_ULTRA_LIGHT,
        color: colors.GREEN,
        borderColor: colors.GREEN,
        '&:hover': { bgcolor: colors.GREEN_ULTRA_LIGHT },
      },
    },
  },
  primaryBtn: {
    bgcolor: colors.GREEN,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 600,
    px: 3,
    '&:hover': { bgcolor: colors.GREEN_LEAF },
  },
};

const buildPayload = values => ({
  title: values.title.trim(),
  description: values.description,
  // Both timestamps share the same calendar day; only the times differ.
  startDateTime: localToUtcIso(values.startDate, values.startTime),
  endDateTime: localToUtcIso(values.startDate, values.endTime),
  instructorUserId: values.instructorUserId,
  instructorName: values.instructorName,
  location: values.location,
});

const sessionToFormValues = session => {
  if (!session) return sessionFormDefaultValues;
  const start = utcIsoToLocalParts(session.startDateTime);
  const end = utcIsoToLocalParts(session.endDateTime);
  return {
    title: session.title || '',
    description: session.description || null,
    startDate: start.date,
    startTime: start.time,
    endTime: end.time, // assume same-day session; the date comes from start
    instructorUserId: session.instructorUserId || null,
    instructorName: session.instructorName || null,
    location: session.location || null,
  };
};

/**
 * Centered dialog (not fullScreen) for create / edit of a session.
 *
 * Scope: only the session metadata. Attendance management (add / remove
 * employees) lives in `SessionDetailDialog` after creation — this keeps the
 * form lightweight and the create-flow a single POST.
 */
export default function SessionFormDialog({
  open,
  mode,
  courseId,
  initialSession,
  onClose,
  onSubmitSession,
  submitting,
}) {
  const { t2 } = useTranslation('EditCourseSessions');
  const [serverError, setServerError] = useState(null);
  const [instructorUser, setInstructorUser] = useState(null);
  const [instructorType, setInstructorType] = useState('user');
  const [courseName, setCourseName] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(sessionFormSchema),
    defaultValues: sessionFormDefaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  // Sync the form whenever the dialog opens or the editing target changes.
  useEffect(() => {
    if (!open) return;
    setServerError(null);
    reset(sessionToFormValues(initialSession));
    setInstructorUser(initialSession?.instructor || null);
    if (initialSession?.instructorUserId) setInstructorType('user');
    else if (initialSession?.instructorName) setInstructorType('external');
    else setInstructorType('user');
  }, [open, initialSession, reset]);

  // Fetch the course reference data (name) when the dialog opens.
  useEffect(() => {
    if (!open || !courseId) return;
    let cancelled = false;
    (async () => {
      const response = await coursesApi.readUnique(courseId);
      if (!cancelled && response.data) {
        const course = response.data;
        setCourseName(course.name || course.title || '');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, courseId]);

  const handleInstructorTypeChange = (_event, next) => {
    if (!next || next === instructorType) return;
    setInstructorType(next);
    if (next === 'user') {
      setValue('instructorName', null);
    } else {
      setInstructorUser(null);
      setValue('instructorUserId', null);
    }
  };

  const onSubmit = async values => {
    setServerError(null);
    const payload = buildPayload(values);
    if (mode === 'create') payload.courseId = courseId;
    const response = await onSubmitSession(payload);
    if (response?.error) {
      setServerError(response.error);
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: styles.paper }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={styles.titleRow}>
          <Typography component="h2" sx={styles.title}>
            {mode === 'edit' ? t2('form.editTitle') : t2('form.createTitle')}
          </Typography>
          {mode === 'create' && <Chip label={t2('form.draftBadge')} size="small" sx={styles.draftPill} />}
        </Box>
        <Typography sx={styles.subtitle}>
          {t2('form.linkedToCoursePrefix')}{' '}
          <Box component="span" sx={styles.subtitleAccent}>
            {courseName || '…'}
          </Box>
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box component="form" id="session-form" onSubmit={handleSubmit(onSubmit)}>
          {serverError && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {serverError}
            </Alert>
          )}

          <Stack spacing={2.5}>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t2('form.title')}
                  required
                  fullWidth
                  size="small"
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  inputProps={{ maxLength: 255 }}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  label={t2('form.description')}
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  error={!!errors.description}
                  helperText={errors.description?.message || `${(field.value || '').length} / 2000`}
                  inputProps={{ maxLength: 2000 }}
                />
              )}
            />

            <Box sx={styles.rowDate}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    label={t2('form.date')}
                    required
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.startDate}
                    helperText={errors.startDate?.message}
                  />
                )}
              />
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="time"
                    label={t2('form.startTime')}
                    required
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.startTime}
                    helperText={errors.startTime?.message}
                  />
                )}
              />
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="time"
                    label={t2('form.endTime')}
                    required
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.endTime}
                    helperText={errors.endTime?.message}
                  />
                )}
              />
            </Box>

            <Box>
              <Typography sx={styles.fieldLabel}>{t2('form.instructorLabel')}</Typography>
              <ToggleButtonGroup
                value={instructorType}
                exclusive
                onChange={handleInstructorTypeChange}
                size="small"
                sx={styles.toggleGroup}
              >
                <ToggleButton value="user">{t2('form.instructorTypeUser')}</ToggleButton>
                <ToggleButton value="external">{t2('form.instructorTypeExternal')}</ToggleButton>
              </ToggleButtonGroup>

              {instructorType === 'user' ? (
                <Controller
                  name="instructorUserId"
                  control={control}
                  render={({ field }) => (
                    <UserAutocomplete
                      value={instructorUser}
                      onChange={user => {
                        setInstructorUser(user);
                        field.onChange(user?.id || null);
                      }}
                      label={t2('form.instructorUserPlaceholder')}
                      placeholder={t2('form.instructorUserPlaceholder')}
                      error={!!errors.instructorName || !!errors.instructorUserId}
                      helperText={errors.instructorName?.message || ''}
                    />
                  )}
                />
              ) : (
                <Controller
                  name="instructorName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label={t2('form.instructorExternalPlaceholder')}
                      fullWidth
                      size="small"
                      helperText={errors.instructorName?.message || ''}
                      error={!!errors.instructorName}
                    />
                  )}
                />
              )}
            </Box>

            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  label={t2('form.location')}
                  fullWidth
                  size="small"
                  error={!!errors.location}
                  helperText={errors.location?.message}
                  inputProps={{ maxLength: 255 }}
                />
              )}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: colors.GRAY_DARKER }}>
          {t2('form.cancel')}
        </Button>
        <Button
          type="submit"
          form="session-form"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={submitting}
          sx={styles.primaryBtn}
          disableElevation
        >
          {t2('form.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SessionFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  courseId: PropTypes.string,
  initialSession: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmitSession: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

SessionFormDialog.defaultProps = {
  courseId: null,
  initialSession: null,
  submitting: false,
};
